import { AfterContentInit, Component, ElementRef, EventEmitter, Input, Output, Renderer2, OnDestroy } from '@angular/core';

import connectSearchBox from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import connectHits from 'instantsearch.js/es/connectors/hits/connectHits';
import connectRefinementList from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import connectConfigure from 'instantsearch.js/es/connectors/configure/connectConfigure';
import InstantSearch from 'instantsearch.js/es/lib/InstantSearch';

import { noop } from '../../../utils/utils';
import { AlgoliaHit, AlgoliaInstanceNames } from '../../constants/algolia';
import { BaseAlgoliaComponent } from '../../../utils/algolia';
import { CommonProvider } from '../../services/common';

@Component({
  selector: 'algolia-search-chip-select',
  templateUrl: './algolia-search-chip-select.html',
  styleUrls: ['algolia-search-chip-select.scss'],
  standalone: false,
})
export class AlgoliaSearchChipSelect extends BaseAlgoliaComponent implements AfterContentInit, OnDestroy {
  @Input() instanceName: AlgoliaInstanceNames = AlgoliaInstanceNames.open_house_scheduler_search_paired_agents;
  @Input() placeholderText = 'Search for agents';
  @Input() noResultsMessage = 'No matches found. Make sure this agent is part of your team plan.';

  @Output() onResultClicked = new EventEmitter<AlgoliaHit>();

  // Algolia related fields
  instance: InstantSearch;
  refine: (query: string) => void;
  query: string;
  hits: AlgoliaHit[] = [];

  searchText: string;
  isInputDirty = false;

  selectedAgent: AlgoliaHit;
  selectedAgentProfileImageURL: string;
  selectedAgentInitials: string;

  private documentClickListener: () => void;

  constructor(private common: CommonProvider, private elRef: ElementRef, private renderer: Renderer2) {
    super();
  }

  async ngAfterContentInit() {
    const { instance, configs } = await this.createInstance(this.instanceName);

    this.instance = instance;
    const { refinements = [], searchParameters = {} } = configs;

    this.instance.addWidgets([
      connectConfigure(noop)({
        searchParameters,
      }),
      connectSearchBox((state) => {
        this.refine = state.refine;
        this.query = state.query;
      })({}),
      connectHits(({ hits }) => {
        this.hits = hits as any;
      })({}),
      ...refinements.map((refinement) => {
        return connectRefinementList(noop)({
          attribute: refinement,
          operator: 'and',
          limit: 10000,
        });
      }),
    ]);

    // Setting the query to `undefined` will prevent the initial search to run. It's ignored by the `searchFunction` callback
    this.instance.helper.setQuery(undefined);

    this.documentClickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      // If the user clicks outside the component, we hide the results
      if (!this.elRef.nativeElement.contains(event.target)) {
        this.hits = [];
        // Reset the input state so that we don't show the no results label on click outside
        this.isInputDirty = false;
      }
    });
  }

  ngOnDestroy() {
    if (this.documentClickListener) {
      this.documentClickListener();
    }
  }

  onInputChange(text: string) {
    this.isInputDirty = true;
    if (text === '') {
      this.clearSearch();
    } else {
      this.searchText = text;
      this.refine(this.searchText);
    }
  }

  onInputFocus() {
    // If the user clicks on the input while empty, trigger a search
    if (!this.searchText && this.hits.length === 0) {
      this.refine('');
    }
  }

  clearSearch(event?) {
    if (event) {
      event.preventDefault();
    }

    this.searchText = undefined;
    this.hits = [];
  }

  displayNoResultsLabel() {
    return this.searchText && this.instance?.status === 'idle' && this.isInputDirty && this.hits.length === 0 && !this.selectedAgent;
  }

  async resultClicked(hit: AlgoliaHit) {
    this.onResultClicked.emit(hit);
    this.clearSearch();

    await this.selectAgent(hit);
  }

  public async selectAgent(agent: AlgoliaHit, _imageURL?: string, _initials?: string) {
    this.selectedAgent = agent;

    if (_imageURL) {
      this.selectedAgentProfileImageURL = _imageURL;
      return;
    } else if (_initials) {
      this.selectedAgentInitials = _initials;
      return;
    }

    const { imageURL, initials } = await this.common.withLoading(() => this.common.getAgentProfileImageURLAndInitials(agent.objectID));

    if (imageURL) {
      this.selectedAgentProfileImageURL = imageURL;
    } else {
      this.selectedAgentInitials = initials;
    }
  }

  removeSelectedAgent() {
    this.onResultClicked.emit(undefined);
    this.selectedAgent = undefined;
    this.selectedAgentProfileImageURL = undefined;
    this.selectedAgentInitials = undefined;
  }
}
